import { ApiError } from '../../identity/types';
import { friendlyErrorMessage } from '../../../shared/friendlyError';
import { isSubscriptionRestrictionCode } from '../../../shared/subscriptionRestriction';

export const BILLING_UNAVAILABLE_MESSAGE = 'Billing is not available in this environment.';

/** A plain permission refusal (the caller's role lacks the billing permission). NOT a subscription-state refusal, which
 * is a different 403 with its own code and its own notice. The server's 403 is authoritative. */
export function isPermissionDenied(err: unknown): boolean {
  return err instanceof ApiError && err.status === 403 && !isSubscriptionRestrictionCode(err.code);
}

/** The server has no billing provider configured (`503 BILLING_UNAVAILABLE`). */
export function isBillingUnavailable(err: unknown): boolean {
  return err instanceof ApiError && err.status === 503 && err.code === 'BILLING_UNAVAILABLE';
}

/** Plain-language copy for a failed billing call. Technical detail goes to the console through `friendlyErrorMessage`,
 * never to the screen. */
export function billingErrorMessage(err: unknown, fallback: string): string {
  if (isBillingUnavailable(err)) return BILLING_UNAVAILABLE_MESSAGE;
  if (err instanceof ApiError) {
    switch (err.code) {
      case 'TRIAL_ALREADY_USED':
        return 'This company has already used its free trial. You can subscribe instead.';
      case 'SUBSCRIPTION_EXISTS':
        return 'This company already has a subscription. Refresh the page to see its current state.';
      case 'PREVIEW_CHANGED':
        return 'The amount changed since you reviewed it. Please review the new amount and confirm again.';
      default:
        break;
    }
    if (isPermissionDenied(err)) {
      return 'You have read-only access to billing. Ask a company administrator with billing permission to make this change.';
    }
  }
  return friendlyErrorMessage(err, fallback);
}
